import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

type SetupOption = "new" | "import";

interface OnboardingSetupShopContentProps {
  selectedOption: SetupOption | null;
  setSelectedOption: (option: SetupOption) => void;
  isShopContext?: boolean;
}

const OnboardingSetupShopContent: React.FC<OnboardingSetupShopContentProps> = ({
  selectedOption,
  setSelectedOption,
  isShopContext = false,
}) => {
  const shopContextConfig = {
    new: {
      title: "Setup a New Shop",
      description: "Guide me through my shop setup.",
      altText: "Setup a New Shop",
    },
    import: {
      title: "Integrate with My Shopify Store",
      description: "We'll sync your Shopify account with Replo",
      altText: "Integrate with My Shopify Store",
    },
  };

  const defaultConfig = {
    new: {
      title: "I'm Brand New",
      description: "Guide me through my store setup.",
      altText: "Brand New",
    },
    import: {
      title: "Import Shop from Shopify",
      description: "We'll pull your theme, products, and store info.",
      altText: "Import Shop from Shopify",
    },
  };

  const config = isShopContext ? shopContextConfig : defaultConfig;

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div
        className={twMerge(
          "flex-1 h-80 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
          selectedOption === "new" ? "border-transparent" : "border-border",
        )}
        onClick={() => setSelectedOption("new")}
      >
        <img
          src="/images/onboarding/step3-bg.png"
          alt=""
          className={twMerge(
            "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
            selectedOption === "new" ? "opacity-100" : "opacity-0",
          )}
        />
        <div className="flex flex-col gap-6 h-full relative z-10">
          <div
            className={twMerge(
              "flex flex-col gap-1",
              isShopContext ? "!text-left" : "",
            )}
            {...(isShopContext && { "data-card-content": true })}
          >
            <div
              className={twMerge(
                "text-lg font-medium transition-colors duration-300",
                selectedOption === "new" ? "" : "text-muted",
              )}
              style={selectedOption === "new" ? { color: "#F5FAD9" } : {}}
            >
              {config.new.title}
            </div>
            <div
              className={twMerge(
                "font-light transition-colors duration-300",
                selectedOption === "new" ? "" : "text-placeholder",
              )}
              style={selectedOption === "new" ? { color: "#F5FAD9" } : {}}
            >
              {config.new.description}
            </div>
          </div>
          <div className="flex-1 flex items-end justify-center">
            <img
              src={
                selectedOption === "new"
                  ? "/images/onboarding/new-shop-color.svg"
                  : "/images/onboarding/brand-new.svg"
              }
              alt={config.new.altText}
              className="w-[384px] h-[400px] max-w-none max-h-none object-contain transform translate-x-3 -translate-y-15 transition-transform duration-400 ease-in-out group-hover:-translate-y-18"
            />
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          "flex-1 h-80 border rounded-xl p-4 group cursor-pointer overflow-hidden relative transition-colors duration-300",
          selectedOption === "import" ? "border-transparent" : "border-border",
        )}
        onClick={() => setSelectedOption("import")}
      >
        <img
          src="/images/onboarding/step3-bg.png"
          alt=""
          className={twMerge(
            "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
            selectedOption === "import" ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={twMerge(
            "absolute transition-all duration-300 bg-no-repeat bg-contain bg-center transform group-hover:translate-x-2",
            selectedOption === "import"
              ? "-left-12 -bottom-8 w-65 h-65"
              : "-left-21 -bottom-19 w-85 h-85",
          )}
          style={{
            backgroundImage: `url(${
              selectedOption === "import"
                ? "/images/onboarding/shopify-circle-color.svg"
                : "/images/onboarding/shopify-circle.svg"
            })`,
          }}
          aria-label="Shopify"
        />
        <div
          className={twMerge(
            "absolute transition-all duration-300 bg-no-repeat bg-contain bg-center transform group-hover:-translate-x-2",
            selectedOption === "import"
              ? "-right-12 -bottom-8 w-65 h-65"
              : "-right-21 -bottom-19 w-85 h-85",
          )}
          style={{
            backgroundImage: `url(${
              selectedOption === "import"
                ? "/images/onboarding/replo-circle-color.svg"
                : "/images/onboarding/replo-circle.svg"
            })`,
          }}
          aria-label="Replo"
        />
        <div className="flex flex-col gap-6 relative z-10">
          <div
            className={twMerge(
              "flex flex-col gap-1",
              isShopContext ? "!text-left" : "",
            )}
            {...(isShopContext && { "data-card-content": true })}
          >
            <div
              className={twMerge(
                "text-lg font-medium transition-colors duration-300",
                selectedOption === "import" ? "" : "text-muted",
              )}
              style={selectedOption === "import" ? { color: "#F5FAD9" } : {}}
            >
              {config.import.title}
            </div>
            <div
              className={twMerge(
                "font-light transition-colors duration-300",
                selectedOption === "import" ? "" : "text-placeholder",
              )}
              style={selectedOption === "import" ? { color: "#F5FAD9" } : {}}
            >
              {config.import.description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSetupShopContent;
export type { SetupOption };
